/**
 * 认证翻译 - 意译风格
 * 登录、注册、重置密码
 */

import type { TranslationDict } from '../types';

export const auth: TranslationDict = {
  // 登录页面 - 意译风格（有温度）
  login: {
    title: {
      en: 'Welcome back',
      'zh-CN': '欢迎回来 👋',
      ja: 'おかえりなさい',
    },
    subtitle: {
      en: 'Sign in to continue',
      'zh-CN': '登录后继续探索',
      ja: 'サインインして続ける',
    },
    emailLabel: {
      en: 'Email',
      'zh-CN': '邮箱地址',
      ja: 'メールアドレス',
    },
    emailPlaceholder: {
      en: 'you@example.com',
      'zh-CN': 'your@email.com',
      ja: 'example@email.com',
    },
    passwordLabel: {
      en: 'Password',
      'zh-CN': '密码',
      ja: 'パスワード',
    },
    passwordPlaceholder: {
      en: 'Enter your password',
      'zh-CN': '输入密码',
      ja: 'パスワードを入力',
    },
    submit: {
      en: 'Sign in',
      'zh-CN': '登录',
      ja: 'ログイン',
    },
    submitting: {
      en: 'Signing in...',
      'zh-CN': '登录中...',
      ja: 'ログイン中...',
    },
    forgotPassword: {
      en: 'Forgot password?',
      'zh-CN': '忘记密码？',
      ja: 'パスワードを忘れた？',
    },
    noAccount: {
      en: "Don't have an account?",
      'zh-CN': '还没有账号？',
      ja: 'アカウントをお持ちでない？',
    },
    signUp: {
      en: 'Create account',
      'zh-CN': '立即注册',
      ja: '新規登録',
    },
    or: {
      en: 'or',
      'zh-CN': '或者',
      ja: 'または',
    },
    // 意译：更友好的第三方登录文案
    continueWithGoogle: {
      en: 'Continue with Google',
      'zh-CN': '使用 Google 登录',
      ja: 'Googleで続ける',
    },
    continueWithX: {
      en: 'Continue with X',
      'zh-CN': '使用 X 登录',
      ja: 'Xで続ける',
    },
    close: {
      en: 'Close',
      'zh-CN': '关闭',
      ja: '閉じる',
    },
  },
  
  // 注册页面
  register: {
    title: {
      en: 'Create your account',
      'zh-CN': '创建账号 🚀',
      ja: 'アカウント作成',
    },
    subtitle: {
      en: 'Join our community today',
      'zh-CN': '加入我们，开启新体验',
      ja: 'コミュニティに参加しましょう',
    },
    usernameLabel: {
      en: 'Username',
      'zh-CN': '用户名',
      ja: 'ユーザー名',
    },
    usernamePlaceholder: {
      en: 'Choose a unique username',
      'zh-CN': '取个独特的用户名',
      ja: 'ユニークな名前を選んでください',
    },
    displayNameLabel: {
      en: 'Display name',
      'zh-CN': '显示名称',
      ja: '表示名',
    },
    displayNamePlaceholder: {
      en: 'Your Name',
      'zh-CN': '你的昵称',
      ja: 'あなたの名前',
    },
    emailLabel: {
      en: 'Email',
      'zh-CN': '邮箱',
      ja: 'メールアドレス',
    },
    passwordLabel: {
      en: 'Password',
      'zh-CN': '设置密码',
      ja: 'パスワード',
    },
    passwordHint: {
      en: 'At least 8 characters',
      'zh-CN': '至少8个字符',
      ja: '8文字以上',
    },
    submit: {
      en: 'Create account',
      'zh-CN': '创建账号',
      ja: 'アカウント作成',
    },
    submitting: {
      en: 'Creating account...',
      'zh-CN': '创建中...',
      ja: '作成中...',
    },
    hasAccount: {
      en: 'Already have an account?',
      'zh-CN': '已有账号？',
      ja: 'すでにアカウントをお持ち？',
    },
    signIn: {
      en: 'Sign in',
      'zh-CN': '直接登录',
      ja: 'ログイン',
    },
  },
  
  // 重置密码
  resetPassword: {
    title: {
      en: 'Reset password',
      'zh-CN': '重置密码',
      ja: 'パスワードリセット',
    },
    subtitle: {
      en: "Enter your email and we'll send you a reset link",
      'zh-CN': '输入邮箱，我们会发送重置链接',
      ja: 'メールアドレスを入力すると、リセットリンクを送信します',
    },
    emailLabel: {
      en: 'Email address',
      'zh-CN': '注册邮箱',
      ja: '登録メールアドレス',
    },
    submit: {
      en: 'Send reset link',
      'zh-CN': '发送重置链接',
      ja: 'リンクを送信',
    },
    submitting: {
      en: 'Sending...',
      'zh-CN': '发送中...',
      ja: '送信中...',
    },
    success: {
      en: 'Check your inbox! 📧',
      'zh-CN': '邮件已发送，请查收',
      ja: 'メールを送信しました。受信箱をご確認ください',
    },
    successDesc: {
      en: "We've sent you an email with instructions to reset your password",
      'zh-CN': '我们已发送密码重置说明到您的邮箱',
      ja: 'パスワードリセットの手順をメールでお送りしました',
    },
    backToLogin: {
      en: 'Back to sign in',
      'zh-CN': '返回登录',
      ja: 'ログインに戻る',
    },
    verifyingLink: {
      en: 'Verifying reset link...',
      'zh-CN': '正在验证重置链接...',
      ja: 'リセットリンクを確認中...',
    },
    linkExpiredTitle: {
      en: 'Link Expired',
      'zh-CN': '链接已过期',
      ja: 'リンクの有効期限が切れました',
    },
    linkExpiredDesc: {
      en: 'This reset link has expired. Reset links are valid for 24 hours.',
      'zh-CN': '这个重置链接已过期。重置链接有效期为 24 小时。',
      ja: 'このリセットリンクは期限切れです。リンクの有効期限は24時間です。',
    },
    requestNewLink: {
      en: 'Request New Link',
      'zh-CN': '申请新链接',
      ja: '新しいリンクをリクエスト',
    },
    resetSuccessTitle: {
      en: 'Password Reset Successfully',
      'zh-CN': '密码重置成功',
      ja: 'パスワードのリセットに成功しました',
    },
    redirectingToLogin: {
      en: 'Redirecting to login in 3s...',
      'zh-CN': '3 秒后跳转到登录页...',
      ja: '3秒後にログイン画面へ移動します...',
    },
    goToLogin: {
      en: 'Go to Login',
      'zh-CN': '前往登录',
      ja: 'ログインへ進む',
    },
    createStrongPassword: {
      en: 'Create a strong password for your account',
      'zh-CN': '为你的账号创建一个强密码',
      ja: 'アカウント用の強力なパスワードを設定してください',
    },
    setNewPasswordTitle: {
      en: 'Set new password',
      'zh-CN': '设置新密码',
      ja: '新しいパスワードを設定',
    },
    setNewPasswordDesc: {
      en: 'Enter your new password below.',
      'zh-CN': '在下方输入你的新密码。',
      ja: '新しいパスワードを入力してください。',
    },
    newPasswordLabel: {
      en: 'New Password',
      'zh-CN': '新密码',
      ja: '新しいパスワード',
    },
    newPasswordPlaceholder: {
      en: 'At least 8 characters',
      'zh-CN': '至少 8 个字符',
      ja: '8文字以上',
    },
    confirmPasswordLabel: {
      en: 'Confirm Password',
      'zh-CN': '确认密码',
      ja: 'パスワード確認',
    },
    confirmPasswordPlaceholder: {
      en: 'Re-enter your new password',
      'zh-CN': '请再次输入新密码',
      ja: '新しいパスワードを再入力',
    },
    submitReset: {
      en: 'Reset Password',
      'zh-CN': '重置密码',
      ja: 'パスワードをリセット',
    },
    submittingReset: {
      en: 'Resetting...',
      'zh-CN': '重置中...',
      ja: 'リセット中...',
    },
    signingIn: {
      en: 'Signing in...',
      'zh-CN': '登录中...',
      ja: 'ログイン中...',
    },
  },
  
  // 验证相关
  validation: {
    emailRequired: {
      en: 'Please enter your email',
      'zh-CN': '请输入邮箱地址',
      ja: 'メールアドレスを入力してください',
    },
    emailInvalid: {
      en: 'Please enter a valid email',
      'zh-CN': '邮箱格式不正确',
      ja: '有効なメールアドレスを入力してください',
    },
    passwordRequired: {
      en: 'Please enter your password',
      'zh-CN': '请输入密码',
      ja: 'パスワードを入力してください',
    },
    passwordTooShort: {
      en: 'Password must be at least 8 characters',
      'zh-CN': '密码至少需要8位',
      ja: 'パスワードは8文字以上必要です',
    },
    passwordMissingUppercase: {
      en: 'Password must contain at least one uppercase letter',
      'zh-CN': '密码必须至少包含一个大写字母',
      ja: 'パスワードには大文字を1文字以上含めてください',
    },
    passwordMissingLowercase: {
      en: 'Password must contain at least one lowercase letter',
      'zh-CN': '密码必须至少包含一个小写字母',
      ja: 'パスワードには小文字を1文字以上含めてください',
    },
    passwordMissingNumber: {
      en: 'Password must contain at least one number',
      'zh-CN': '密码必须至少包含一个数字',
      ja: 'パスワードには数字を1文字以上含めてください',
    },
    passwordsNotMatch: {
      en: 'Passwords do not match',
      'zh-CN': '两次输入的密码不一致',
      ja: 'パスワードが一致しません',
    },
    resetLinkExpired: {
      en: 'Reset link has expired. Please request a new one.',
      'zh-CN': '重置链接已过期，请重新申请。',
      ja: 'リセットリンクの有効期限が切れています。再度リクエストしてください。',
    },
    usernameRequired: {
      en: 'Please choose a username',
      'zh-CN': '请设置用户名',
      ja: 'ユーザー名を設定してください',
    },
  },
  
  // 错误提示 - 意译（友好）
  errors: {
    invalidCredentials: {
      en: 'Email or password is incorrect',
      'zh-CN': '邮箱或密码不正确',
      ja: 'メールアドレスまたはパスワードが正しくありません',
    },
    accountNotFound: {
      en: "We couldn't find an account with that email",
      'zh-CN': '找不到该邮箱对应的账号',
      ja: 'そのメールアドレスのアカウントが見つかりません',
    },
    emailInUse: {
      en: 'This email is already registered',
      'zh-CN': '该邮箱已注册',
      ja: 'このメールアドレスは既に登録されています',
    },
    usernameTaken: {
      en: 'This username is already taken',
      'zh-CN': '该用户名已被使用',
      ja: 'このユーザー名は既に使用されています',
    },
    tooManyAttempts: {
      en: 'Too many attempts. Please try again later.',
      'zh-CN': '尝试次数过多，请稍后再试',
      ja: '試行回数が多すぎます。しばらくしてからお試しください',
    },
    resetFailed: {
      en: 'Reset failed. Please request a new reset link.',
      'zh-CN': '重置失败，请重新申请重置链接。',
      ja: 'リセットに失敗しました。新しいリンクをリクエストしてください。',
    },
  },
};
